import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Paper,
  InputAdornment,
  Stack,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useEvaluationSettings } from '../hooks/useEvaluation';
import { evaluationService } from '../services/evaluationService';

function formatTimestamp(timestamp) {
  if (!timestamp) return 'N/A';
  try {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${day}. ${month}. ${year} ${hours}:${minutes}:${seconds}`;
  } catch (e) {
    console.error('Error formatting timestamp:', e);
    return 'Invalid date';
  }
}

function formatDateForInput(dateString) {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  } catch (e) {
    return '';
  }
}

const allAvailableProperties = [
  "voc", "fmhd", "temp", "hum", "co2", "pm1_0", "pm2_5",
  "pm4_0", "pm10", "mass_c", "o3", "no2", "co", "nh3"
];

export default function EvaluationSettingsPanel({ boxId }) {
  const { data: settings, loading, error, refetch } = useEvaluationSettings(boxId);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSettings, setEditedSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [useRealtime, setUseRealtime] = useState(true);
  const [customStartTime, setCustomStartTime] = useState('');
  const [newProperty, setNewProperty] = useState('');

  const handleEdit = () => {
    if (settings) {
      setEditedSettings({
        ...settings,
        anomalyDetectorState: settings.anomalyDetectorState || 'inference',
        anomalyDetectorVersion: settings.anomalyDetectorVersion || 'latest',
        substanceClassifierState: settings.substanceClassifierState || 'inference',
        trendQualityAnalysingIntervalHr: settings.trendQualityAnalysingIntervalHr || 8,
        observedSurfaceTemperatureCelsius: settings.observedSurfaceTemperatureCelsius || 17,
        observedPropertiesAll: settings.observedPropertiesAll || [],
        observedPropertiesAirQualityExcluded: settings.observedPropertiesAirQualityExcluded || []
      });
    }
    setIsEditing(true);
    setSaveError(null);

    if (settings?.startTsDataReading) {
      const isCurrentTime = Math.abs(new Date(settings.startTsDataReading).getTime() - Date.now()) < 60000;
      setUseRealtime(isCurrentTime);
      if (!isCurrentTime) {
        setCustomStartTime(formatDateForInput(settings.startTsDataReading));
      }
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedSettings(null);
    setUseRealtime(true);
    setCustomStartTime('');
    setSaveError(null);
    setNewProperty('');
  };

  const handleSave = async () => {
    if (!editedSettings) return;

    setSaving(true);
    setSaveError(null);

    try {
      const saveData = {
        ...editedSettings,
        startTsDataReading: useRealtime
          ? new Date().toISOString()
          : customStartTime
            ? new Date(customStartTime).toISOString()
            : editedSettings.startTsDataReading
      };

      await evaluationService.updateEvaluationSettings(boxId, saveData);
      await refetch();
      setIsEditing(false);
      setEditedSettings(null);
      setUseRealtime(true);
      setCustomStartTime('');
      setNewProperty('');
    } catch (err) {
      setSaveError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    if (!editedSettings) return;
    setEditedSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addProperty = () => {
    if (newProperty && !editedSettings.observedPropertiesAll.includes(newProperty)) {
      handleFieldChange('observedPropertiesAll', [...editedSettings.observedPropertiesAll, newProperty]);
      setNewProperty('');
    }
  };

  const removeProperty = (prop) => {
    handleFieldChange('observedPropertiesAll',
      editedSettings.observedPropertiesAll.filter(p => p !== prop)
    );
    handleFieldChange('observedPropertiesAirQualityExcluded',
      editedSettings.observedPropertiesAirQualityExcluded.filter(p => p !== prop)
    );
  };

  const toggleExcluded = (prop) => {
    const excluded = editedSettings.observedPropertiesAirQualityExcluded;
    if (excluded.includes(prop)) {
      handleFieldChange('observedPropertiesAirQualityExcluded', excluded.filter(p => p !== prop));
    } else {
      handleFieldChange('observedPropertiesAirQualityExcluded', [...excluded, prop]);
    }
  };

  const getCurrentData = () => {
    return isEditing && editedSettings ? editedSettings : settings;
  };

  if (loading) {
    return (
      <Card sx={{ width: '100%', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>Loading evaluation settings...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ width: '100%', mb: 3 }}>
        <CardContent>
          <Alert severity="error">
            Error loading evaluation settings: {error.message || 'Unknown error'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card sx={{ width: '100%', mb: 3 }}>
        <CardContent>
          <Alert severity="info">
            No evaluation settings available for this box
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const currentData = getCurrentData();

  if (isEditing && !currentData) {
    return (
      <Card sx={{ width: '100%', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>Preparing edit mode...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ width: '100%', mb: 3 }}>
      <CardContent>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SettingsIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Evaluation Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure analysis module parameters
              </Typography>
            </Box>
          </Box>

          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              size="medium"
            >
              Edit Settings
            </Button>
          ) : (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={saving}
                size="medium"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                size="medium"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Stack>
          )}
        </Box>

        {saveError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {saveError}
          </Alert>
        )}

        {isEditing && currentData && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: 'grey.50',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Analysis Start Time
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={useRealtime}
                  onChange={(e) => {
                    setUseRealtime(e.target.checked);
                    if (e.target.checked) {
                      setCustomStartTime('');
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Start from current time (real-time analysis)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Begin analysis immediately with latest data
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />

            {!useRealtime && (
              <Box sx={{ ml: 4 }}>
                <TextField
                  fullWidth
                  label="Custom start time"
                  type="datetime-local"
                  value={customStartTime}
                  onChange={(e) => setCustomStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Select specific start time for historical analysis"
                  sx={{ maxWidth: 500 }}
                />
              </Box>
            )}
          </Paper>
        )}

        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 3,
          overflowX: 'auto',
          pb: 2
        }}>
          {!isEditing && currentData && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                minWidth: 280,
                flexShrink: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <AccessTimeIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Time Settings
                </Typography>
              </Box>

              <Stack spacing={3}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Start TS Data Reading
                    </Typography>
                    <Tooltip title="Time from which analysis begins">
                      <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatTimestamp(currentData.startTsDataReading)}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      TS Data Updated At
                    </Typography>
                    <Tooltip title="When settings were last saved">
                      <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatTimestamp(currentData.tsDataUpdatedAt)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 3,
              minWidth: 280,
              flexShrink: 0,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3 }}>
              Module States
            </Typography>

            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
                  Anomaly Detector
                </Typography>
                {isEditing && currentData ? (
                  <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>State</InputLabel>
                      <Select
                        value={currentData.anomalyDetectorState || 'inference'}
                        onChange={(e) => handleFieldChange('anomalyDetectorState', e.target.value)}
                        label="State"
                      >
                        <MenuItem value="inference">Inference - Detect anomalies</MenuItem>
                        <MenuItem value="training">Training - Train model</MenuItem>
                        <MenuItem value="disabled">Disabled - Not active</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      size="small"
                      label="Version"
                      value={currentData.anomalyDetectorVersion || 'latest'}
                      onChange={(e) => handleFieldChange('anomalyDetectorVersion', e.target.value)}
                      helperText="Model version for anomaly detection"
                    />
                  </Stack>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={currentData?.anomalyDetectorState || 'N/A'}
                      color={
                        currentData?.anomalyDetectorState === 'inference' ? 'success' :
                        currentData?.anomalyDetectorState === 'training' ? 'warning' :
                        'default'
                      }
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Version: {currentData?.anomalyDetectorVersion || 'latest'}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
                  Substance Classifier
                </Typography>
                {isEditing && currentData ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>State</InputLabel>
                    <Select
                      value={currentData.substanceClassifierState || 'inference'}
                      onChange={(e) => handleFieldChange('substanceClassifierState', e.target.value)}
                      label="State"
                    >
                      <MenuItem value="inference">Inference - Classify substances</MenuItem>
                      <MenuItem value="training">Training - Train model</MenuItem>
                      <MenuItem value="disabled">Disabled - Not active</MenuItem>
                    </Select>
                  </FormControl>
                ) : currentData ? (
                  <Chip
                    label={currentData?.substanceClassifierState || 'N/A'}
                    color={
                      currentData?.substanceClassifierState === 'inference' ? 'success' :
                      currentData?.substanceClassifierState === 'training' ? 'warning' :
                      'default'
                    }
                    size="small"
                  />
                ) : (
                  <Chip
                    label="N/A"
                    color="default"
                    size="small"
                  />
                )}
              </Box>
            </Stack>
          </Paper>

          {currentData && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                minWidth: 280,
                flexShrink: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <ThermostatIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Analysis Parameters
                </Typography>
              </Box>

              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
                    Trend Quality Analysing Interval
                  </Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={currentData.trendQualityAnalysingIntervalHr || 8}
                      onChange={(e) => handleFieldChange('trendQualityAnalysingIntervalHr', parseInt(e.target.value) || 0)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                        inputProps: { min: 1, max: 168 }
                      }}
                      helperText="Time window for trend analysis"
                    />
                  ) : (
                    <Typography variant="body1">
                      {currentData.trendQualityAnalysingIntervalHr || 8} hours
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
                    Observed Surface Temperature
                  </Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={currentData.observedSurfaceTemperatureCelsius || 17}
                      onChange={(e) => handleFieldChange('observedSurfaceTemperatureCelsius', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">°C</InputAdornment>,
                        inputProps: { step: 0.1 }
                      }}
                      helperText="Temperature of nearest wall surface"
                    />
                  ) : (
                    <Typography variant="body1">
                      {currentData.observedSurfaceTemperatureCelsius || 17}°C
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          )}

          {currentData && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                minWidth: 320,
                flexShrink: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3 }}>
                Observed Properties
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
                    All Measured Properties
                  </Typography>

                  {isEditing && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <Select
                          value={newProperty}
                          onChange={(e) => setNewProperty(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select property to add...</em>
                          </MenuItem>
                          {allAvailableProperties
                            .filter(p => !currentData.observedPropertiesAll.includes(p))
                            .map(prop => (
                              <MenuItem key={prop} value={prop}>{prop}</MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                      <Button
                        onClick={addProperty}
                        disabled={!newProperty}
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                      >
                        Add
                      </Button>
                    </Box>
                  )}

                  <Box sx={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50'
                  }}>
                    {currentData.observedPropertiesAll && currentData.observedPropertiesAll.length > 0 ? (
                      currentData.observedPropertiesAll.map((prop, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                            px: 2,
                            borderBottom: idx < currentData.observedPropertiesAll.length - 1 ? 1 : 0,
                            borderColor: 'divider',
                            '&:hover': {
                              bgcolor: isEditing ? 'grey.100' : 'grey.50'
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              color: currentData.observedPropertiesAirQualityExcluded?.includes(prop)
                                ? 'warning.main'
                                : 'text.primary'
                            }}
                          >
                            {prop}
                          </Typography>
                          {isEditing && (
                            <IconButton
                              size="small"
                              onClick={() => removeProperty(prop)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        No properties configured
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
                    Air Quality Excluded Properties
                  </Typography>
                  {isEditing ? (
                    <FormControl fullWidth size="small">
                      <InputLabel>Select properties to exclude</InputLabel>
                      <Select
                        multiple
                        value={currentData.observedPropertiesAirQualityExcluded || []}
                        onChange={(e) => handleFieldChange('observedPropertiesAirQualityExcluded', e.target.value)}
                        label="Select properties to exclude"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                None selected
                              </Typography>
                            ) : (
                              selected.map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  size="small"
                                  color="warning"
                                />
                              ))
                            )}
                          </Box>
                        )}
                      >
                        {currentData.observedPropertiesAll && currentData.observedPropertiesAll.map((prop) => (
                          <MenuItem key={prop} value={prop}>
                            {prop}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Box sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'grey.50',
                      p: 1
                    }}>
                      {(currentData.observedPropertiesAirQualityExcluded &&
                        currentData.observedPropertiesAirQualityExcluded.length > 0) ? (
                        currentData.observedPropertiesAirQualityExcluded.map((prop, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              color: 'warning.main',
                              py: 0.5,
                              px: 1
                            }}
                          >
                            {prop}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          All properties included in air quality analysis
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Stack>
            </Paper>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}